import { fabric } from 'fabric';
import { useCallback } from 'react';
import Vec2 from 'vec2';

import { useDropHandler } from './useDropHandler';
import { useMouseDown } from './useMouseDown';
import { useMouseDownBefore } from './useMouseDownBefore';
import { useMouseUp } from './useMouseUp';
import {
  determineMovementType,
  findFabricObjectByName,
  keepApplianceSelected,
  resetDraggingState,
} from './useMouseUp.utils';

import { CANVAS_CURSOR, CANVAS_OBJECT_TYPE_PREFIX } from '~/core/constants';
import { DRAGGING_STATE } from '~/core/enums';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import useAppliance, { findApplianceInShapes } from '~/core/hooks/useAppliance';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { ApplianceModel, ApplianceType, DrawingShape } from '~/core/models';
import { ApplianceQtyType } from '~/core/types/appliances';
import {
  isCutoutAppliance,
  isRange,
  isShapeValidForApplianceInside,
} from '~/core/utils';
import {
  useApplianceContext,
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { increaseCookingAppliance } from '~/redux/slices/cookingAppliance';
import { increaseCustomCutouts } from '~/redux/slices/cutout';
import {
  resetDraggedAppliance,
  setDraggedAppliance,
} from '~/redux/slices/draggedAppliance';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { setWasEstimateApplianceMoved } from '~/redux/slices/estimate';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { selectSelectedDrawingTab } from '~/redux/slices/shape';

export const useMouseEvents = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);

  const {
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();
  const { updateIsDraggingAppliance, updateOnDropAppliance } =
    useApplianceContext();
  const {
    draggingStateRef,
    drawingShapesRef,
    isShapeLogicRef,
    lastPositionRef,
    movingItemRef,
    shapeStartDragRef,
  } = useFabricCanvasContext();

  const selectedDrawingTab = useAppSelector(selectSelectedDrawingTab);

  const {
    handleApplianceSelection,
    handleCircleClick,
    handleShapeInteraction,
    startDragging,
  } = useMouseDown(canvas);

  const { handleSelectionCircle, handleApplianceInteraction } =
    useMouseDownBefore(canvas);

  const { finalizeDraggingState, handleShapeMovement, syncShapesToContext } =
    useMouseUp();

  const { addApplianceToShape, findApplianceAndShape } = useDropHandler(canvas);
  const { transferApplianceBetweenShapes } = useAppliance();
  const { registerUserApplianceAction } = useRegisterUserAction();

  const increaseCooktopAndRange = useCallback(
    (appliance: ApplianceModel) => {
      if (
        ![ApplianceType.COOKTOP, ApplianceType.RANGE].includes(
          appliance.applianceType
        )
      ) {
        return;
      }

      const isApplianceRange = isRange({
        applianceType: appliance.applianceType,
      });

      const selectedType = isApplianceRange
        ? 'selectedRangeApplianceProduct'
        : 'selectedCooktopApplianceProduct';

      dispatch(
        increaseCookingAppliance({
          selectedType,
          applianceId: appliance.productId,
          qtyType: ApplianceQtyType.onShape,
        })
      );
    },
    [dispatch]
  );

  const increaseElectricalAndFaucetCutouts = useCallback(
    (appliance: ApplianceModel) => {
      if (isCutoutAppliance(appliance)) {
        dispatch(
          increaseCustomCutouts({
            cutoutId: appliance.productId,
            qtyType: ApplianceQtyType.onShape,
          })
        );
      }
    },
    [dispatch]
  );

  const createDropHandler = useCallback(
    (args: {
      appliance: ApplianceModel;
      shapeId: DrawingShape['id'];
      centerPoint: Vec2;
      targetShape: DrawingShape | undefined;
      hasChipMinimizer?: boolean;
    }) =>
      () => {
        const {
          appliance,
          shapeId,
          centerPoint,
          targetShape,
          hasChipMinimizer,
        } = args;

        increaseCooktopAndRange(appliance);
        const _shape = drawingShapesRef.current.find(
          (shapeRef) => shapeId === shapeRef.id
        );

        dispatch(resetDraggedAppliance());
        if (!_shape) {
          return; // Shape not found in drawing shapes
        }

        addApplianceToShape({
          appliance,
          shape: _shape,
          centerPoint,
          targetShape,
          hasChipMinimizer,
        });
        updateDrawingShapes([...drawingShapesRef.current]);
        registerUserApplianceAction({ appliance });
        dispatch(updateNeedRedrawCanvas(true));
      },
    [
      increaseCooktopAndRange,
      addApplianceToShape,
      updateDrawingShapes,
      drawingShapesRef,
      dispatch,
      registerUserApplianceAction,
    ]
  );

  const onDrop = useCallback(
    ({ e }: fabric.IEvent<MouseEvent | DragEvent>) => {
      e.stopPropagation();
      e.preventDefault();

      updateIsDraggingAppliance(false);

      const applianceAndShape = findApplianceAndShape(e as DragEvent);

      if (!applianceAndShape) {
        return;
      }

      const { appliance, shape, targetShape } = applianceAndShape;

      const centerPoint = new Vec2(canvas.getPointer(e));
      const onDropAppliance = createDropHandler({
        appliance,
        shapeId: shape.id,
        centerPoint,
        targetShape,
      });

      const isCookingAppliance = [
        ApplianceType.COOKTOP,
        ApplianceType.RANGE,
      ].includes(appliance.applianceType);
      const shouldStoreForLater =
        !isCutoutAppliance(appliance) && !isCookingAppliance;

      if (shouldStoreForLater) {
        dispatch(
          setDraggedAppliance({
            appliance,
            centerPoint: { x: centerPoint.x, y: centerPoint.y },
            targetShape,
            shapeId: shape.id,
          })
        );
        updateOnDropAppliance(onDropAppliance);
      } else {
        increaseElectricalAndFaucetCutouts(appliance);
        onDropAppliance();
      }
    },
    [
      dispatch,
      updateIsDraggingAppliance,
      findApplianceAndShape,
      canvas,
      createDropHandler,
      updateOnDropAppliance,
      increaseElectricalAndFaucetCutouts,
    ]
  );

  const onMouseDownBefore = useCallback(
    ({ target, ...e }: fabric.IEvent<MouseEvent>) => {
      if (!target) {
        return;
      }

      const targetName = target.name;
      const groupMemberClicked = e.subTargets?.[0];
      const pointer = new Vec2(canvas.getPointer(e.e));

      if (targetName?.includes(CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE)) {
        handleSelectionCircle(pointer);
      } else {
        handleApplianceInteraction(
          target,
          groupMemberClicked,
          targetName,
          pointer
        );
      }
    },
    [canvas, handleSelectionCircle, handleApplianceInteraction]
  );

  const onMouseDown = useCallback(
    ({ target, ...e }: fabric.IEvent<MouseEvent>) => {
      if (target) {
        const targetName = target.name;
        const pointer = new Vec2(canvas.getPointer(e.e));

        if (targetName?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE)) {
          handleApplianceSelection(targetName, pointer);
        } else if (
          targetName?.includes(CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE)
        ) {
          handleCircleClick(target);
        } else if (targetName && isShapeLogicRef.current) {
          handleShapeInteraction(targetName, pointer);
        }
      } else if (selectedDrawingTab !== DrawingTabEnum.FREE_DRAWING_TAB) {
        startDragging(e.e);
      }
    },
    [
      selectedDrawingTab,
      canvas,
      isShapeLogicRef,
      handleApplianceSelection,
      handleCircleClick,
      handleShapeInteraction,
      startDragging,
    ]
  );

  /**
   * Manually calculate deltas for cross-browser compatibility
   * @param mousePosition - the current position of the mouse
   * @param lastPosition - the previous position from the shape
   * @returns the 'delta' difference
   */
  const calculateDelta = (mousePosition: number, lastPosition?: number) =>
    lastPosition ? mousePosition - lastPosition : 0;

  const onMouseMove = useCallback(
    ({ e: mouseEvent }: fabric.IEvent<MouseEvent>) => {
      if (draggingStateRef.current === DRAGGING_STATE.DRAGGING && mouseEvent) {
        const deltaX = calculateDelta(
          mouseEvent.clientX,
          lastPositionRef.current.x
        );

        const deltaY = calculateDelta(
          mouseEvent.clientY,
          lastPositionRef.current.y
        );

        // Update the last X and Y values with mouse position
        lastPositionRef.current = {
          x: mouseEvent.clientX,
          y: mouseEvent.clientY,
        };

        const delta = new fabric.Point(deltaX, deltaY);

        // Update the canvas with the most recent delta values
        canvas.relativePan(delta);
        canvas.defaultCursor = CANVAS_CURSOR.DEFAULT.GRABBING;
      }
    },
    [canvas, draggingStateRef, lastPositionRef]
  );

  const handleApplianceMovement = useCallback(
    (e: MouseEvent) => {
      const applianceId = movingItemRef.current as string;

      const fabricObj = findFabricObjectByName(canvas, applianceId);
      const foundAppliance = findApplianceInShapes(
        applianceId,
        drawingShapesRef.current
      );

      if (!foundAppliance?.appliance || !fabricObj) {
        // Fallback for safety, though this block should not be hit
        syncShapesToContext(fabricObj);
        keepApplianceSelected(canvas, fabricObj);

        return;
      }

      const { appliance: drawingAppliance, shape: sourceShape } =
        foundAppliance;
      const pointer = new Vec2(canvas.getPointer(e));
      const isApplianceRange = isRange(drawingAppliance);
      const shapeUnderPointer = drawingShapesRef.current.find(
        isShapeValidForApplianceInside(
          drawingAppliance.applianceModel.applianceType,
          pointer
        )
      );

      // If the move is not valid (not a range and not over a valid shape), revert position.
      if (!isApplianceRange && !shapeUnderPointer) {
        fabricObj.set({
          left: drawingAppliance.centerPoint.x,
          top: drawingAppliance.centerPoint.y,
        });
        fabricObj.setCoords();
        canvas.requestRenderAll();

        return;
      }

      // If the appliance is moved to a new shape, transfer it in the data model.
      const isMovedToAnotherShape =
        sourceShape &&
        shapeUnderPointer &&
        sourceShape.id !== shapeUnderPointer.id;

      if (isMovedToAnotherShape) {
        transferApplianceBetweenShapes(
          drawingAppliance,
          sourceShape,
          shapeUnderPointer
        );
      }

      // Persist the new visual position and keep the appliance selected.
      syncShapesToContext(fabricObj);
      keepApplianceSelected(canvas, fabricObj);
      dispatch(setWasEstimateApplianceMoved());
    },
    [
      canvas,
      drawingShapesRef,
      movingItemRef,
      syncShapesToContext,
      transferApplianceBetweenShapes,
      dispatch,
    ]
  );

  const onMouseUp = useCallback(
    (e: fabric.IEvent<MouseEvent>) => {
      // Handle movement completion for shapes and appliances
      if (movingItemRef.current) {
        const moveVec = shapeStartDragRef.current.subtract(
          new Vec2(canvas.getPointer(e.e)),
          true
        );
        const movementType = determineMovementType({
          movingItem: movingItemRef.current,
          moveVec,
        });

        switch (movementType) {
          case 'shape':
            handleShapeMovement(moveVec);
            finalizeDraggingState();
            break;
          case 'appliance-moved':
            handleApplianceMovement(e.e);
            break;
          case 'appliance-clicked':
            // Click-only, no state change needed beyond resetting drag state.
            break;
        }
      }

      // ALWAYS reset state at the end - this is critical for proper state cleanup
      resetDraggingState({
        draggingStateRef,
        movingItemRef,
        canvas,
      });
    },
    [
      canvas,
      draggingStateRef,
      finalizeDraggingState,
      handleShapeMovement,
      movingItemRef,
      shapeStartDragRef,
      handleApplianceMovement,
    ]
  );

  return {
    onDrop,
    onMouseDownBefore,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    createDropHandler,
  };
};
